import React, { useState, useEffect } from "react";
import useApi from "../utils/useApi";
import {
  Button,
  Checkbox,
  TextField,
  Grid,
  Box,
  Typography,
  Card,
  CardContent,
  FormControlLabel,
  FormGroup,
  Stack,
} from "@mui/material";

const PermissionGrid = () => {
  const [permissions, setPermissions] = useState([]);
  const [features, setFeatures] = useState([]);
  const [selectedPermission, setSelectedPermission] = useState([]);
  const actions = ["view", "add", "change", "delete"];

  const { Get, Post, getAPI } = useApi();

  useEffect(() => {
    getAllPermissions();
  }, []);

  const getAllPermissions = async () => {
    let allPermissions = [];
    let baseUrl = getAPI("permission");
    let nextPage = `${baseUrl}?page=1`;

    try {
      while (nextPage) {
        const permsResponse = await Get(nextPage);
        allPermissions = [...allPermissions, ...permsResponse.results];
        nextPage = permsResponse.next
          ? `${baseUrl}?page=${new URL(permsResponse.next).searchParams.get(
              "page"
            )}`
          : null;
      }

      const perms = allPermissions.map((permission) => ({
        id: permission.id,
        codename: permission.codename,
      }));
      setPermissions(perms);

      const derivedFeaturesMap = {};
      perms.forEach((permission) => {
        const [action, feature] = permission.codename.split("_");
        if (!derivedFeaturesMap[feature]) {
          derivedFeaturesMap[feature] = {
            title: feature.charAt(0).toUpperCase() + feature.slice(1),
          };
        }
        derivedFeaturesMap[feature][`${feature}_${action}`] = permission.id;
      });
      const derivedFeatures = Object.values(derivedFeaturesMap);
      setFeatures(derivedFeatures);
    } catch (error) {
      console.error("Error fetching permissions:", error);
    }
  };

  const handleCheckboxChange = (permissionId) => {
    setSelectedPermission((prevSelectedPermissions) => {
      if (prevSelectedPermissions.includes(permissionId)) {
        return prevSelectedPermissions.filter((id) => id !== permissionId);
      } else {
        return [...prevSelectedPermissions, permissionId];
      }
    });
  };

  const submitForm = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.target);
    const payload = {
      name: formData.get("name"),
      permissions: selectedPermission,
    };

    try {
      const response = await Post("role", payload);
      console.log("Form submitted successfully", response.data);
      setSelectedPermission([]);
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  return (
    <Box p={2}>
      <Card variant="outlined">
        <CardContent>
          <form onSubmit={submitForm}>
            <TextField
              label="Role Name"
              type="text"
              name="name"
              placeholder="Enter Role Name"
              required
              variant="standard"
              fullWidth
              margin="normal"
              size="small"
            />
            <Grid container spacing={2}>
              {features.map((feature) => (
                <Grid item xs={12} sm={6} md={4} key={feature.title}>
                  <Card elevation={3} sx={{ height: "100%" }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {feature.title}
                      </Typography>
                      <Stack direction="row" spacing={2} alignItems="center">
                        {actions.map((action) => (
                          <FormControlLabel
                            key={action}
                            control={
                              <Checkbox
                                checked={selectedPermission.includes(
                                  feature[
                                    `${feature.title.toLowerCase()}_${action}`
                                  ]
                                )}
                                onChange={() =>
                                  handleCheckboxChange(
                                    feature[
                                      `${feature.title.toLowerCase()}_${action}`
                                    ]
                                  )
                                }
                                size="small"
                              />
                            }
                            label={
                              action.charAt(0).toUpperCase() + action.slice(1)
                            }
                            sx={{ marginLeft: "-12px" }} // Adjust alignment
                          />
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            <Box mt={2} textAlign="center">
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="small"
              >
                Save Permissions
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PermissionGrid;
